import * as React from "react"
import { AlertTriangle, Loader2, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import type { HrisEmployeeFilter, HrisRun } from "@/features/integrations/hris/hrisApi"

type Props = {
  run: HrisRun
  busy: boolean
  onConfirmArchives: () => void
  onOpenEmployees: (filter: HrisEmployeeFilter) => void
}

/**
 * The outcome of the last preview or sync as counters; each one opens the
 * Employees screen on that status. Archives held back by the guard get their
 * own confirm button here.
 */
export function HrisRunCard({ run, busy, onConfirmArchives, onOpenEmployees }: Props) {
  const t = useBookingT()
  const planned = run.stats?.planned
  const preview = run.kind === "preview"

  const tiles: { label: string; value: number; filter: HrisEmployeeFilter }[] = planned
    ? [
        { label: preview ? t("hris_tile_will_create", "Will get an account") : t("hris_tile_create", "New accounts"), value: planned.create, filter: preview ? "pending" : "new" },
        { label: t("hris_tile_update", "Updated or linked"), value: planned.update + planned.link + planned.restore, filter: preview ? "pending" : "synced" },
        { label: preview ? t("hris_tile_will_archive", "Will be archived") : t("hris_tile_archive", "Archived"), value: planned.archive, filter: preview ? "pending" : "archived" },
        { label: t("hris_tile_unchanged", "Up to date"), value: planned.unchanged, filter: "synced" },
        { label: t("hris_tile_attention", "Need attention"), value: planned.conflict + (run.stats?.failed ?? 0), filter: "attention" },
        { label: t("hris_tile_skipped", "Not synced"), value: planned.skip, filter: "skipped" },
      ]
    : []

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-neutral-900">
              {preview ? t("hris_run_preview_title", "Preview") : t("hris_run_sync_title", "Last sync")}
            </h2>
            <p className="text-sm text-neutral-800">
              {preview
                ? t("hris_run_preview_hint", "What the sync would do now. Nothing has been changed.")
                : formatUserDateTime(run.finishedAt ?? run.startedAt)}
            </p>
          </div>
          {run.status === "running" ? (
            <span className="flex items-center gap-2 text-sm text-neutral-800">
              <Loader2 className="size-4 animate-spin text-brand-500" />
              {t("hris_run_running", "Reading the HR system…")}
            </span>
          ) : (
            <Button variant="outline" onClick={() => onOpenEmployees("all")}>
              <Users className="size-4" />
              {t("hris_open_employees", "Open the employees list")}
            </Button>
          )}
        </div>

        {run.status === "failed" && <p className="text-sm text-error-700">{run.error}</p>}

        {run.archiveBlocked && !preview && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-warning-300 bg-warning-50 p-4 max-md:flex-col">
            <span className="flex items-start gap-2 text-sm text-neutral-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-700" />
              {run.archiveBlockReason}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenEmployees("attention")}>
                {t("hris_see_who", "See who")}
              </Button>
              <Button onClick={onConfirmArchives} disabled={busy}>
                {t("hris_confirm_archives", "Archive them")}
              </Button>
            </div>
          </div>
        )}

        {tiles.length > 0 && (
          <div className="grid grid-cols-6 gap-3 max-xl:grid-cols-3 max-md:grid-cols-2">
            {tiles.map((tile) => (
              <button
                key={tile.label}
                type="button"
                onClick={() => onOpenEmployees(tile.filter)}
                className="rounded-md border border-neutral-200 p-3 text-left transition-colors hover:border-brand-500"
              >
                <div className="text-xs uppercase tracking-wide text-neutral-800">{tile.label}</div>
                <div className="mt-1 text-2xl font-semibold text-neutral-900">{tile.value}</div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
